import type {
  ClassificationLevel,
  ProjectView,
  Reader,
  ReaderKind,
  StoredReader,
} from "@esi/shared";
import { t } from "../i18n/i18n.ts";

/**
 * Who may read a document, as the upload form offers it: one audience at a time. Entity
 * audiences name the project's owning entity (the only entity a project member is sure to
 * know); named readers are project members; `groups` are directory groups and attribute rules
 * of the tenant (members still need to read the project).
 */
export type Audience = "project" | "entity" | "entity_tree" | "named" | "groups";

/** Directory principals picked for the `groups` audience. */
export interface PickedPrincipals {
  groups: readonly string[];
  rules: readonly string[];
}

/** Reader kinds each `readersFloor` allows (mirrors the server's rule, which stays the authority). */
const ALLOWED_KINDS: Record<ClassificationLevel["readersFloor"], readonly ReaderKind[]> = {
  project: ["project", "entity", "entity_tree", "user", "group", "attribute_rule"],
  entity: ["entity", "entity_tree", "user", "group", "attribute_rule"],
  named: ["user", "group", "attribute_rule"],
};

const AUDIENCE_KIND: Record<Audience, ReaderKind> = {
  project: "project",
  entity: "entity",
  entity_tree: "entity_tree",
  named: "user",
  groups: "group",
};

/** Audiences this project can offer at all: entity audiences need an owning entity. */
export function projectAudiences(
  project: Pick<ProjectView, "owner">,
  options: { groups?: boolean } = {},
): Audience[] {
  const base: Audience[] =
    project.owner.kind === "entity"
      ? ["project", "entity", "entity_tree", "named"]
      : ["project", "named"];
  return options.groups ? [...base, "groups"] : base;
}

export function audienceAllowed(audience: Audience, level: ClassificationLevel | undefined) {
  return level ? ALLOWED_KINDS[level.readersFloor].includes(AUDIENCE_KIND[audience]) : true;
}

/**
 * The readers to send. Named readers always include the uploader, who must read the file; so do
 * groups and rules, which the uploader may not belong to.
 */
export function audienceReaders(
  audience: Audience,
  project: Pick<ProjectView, "id" | "owner">,
  named: readonly string[],
  uploaderId: string,
  picked: PickedPrincipals = { groups: [], rules: [] },
): Reader[] {
  switch (audience) {
    case "project":
      return [{ kind: "project", ref: project.id }];
    case "entity":
    case "entity_tree":
      if (project.owner.kind !== "entity") {
        throw new Error("Entity audiences need an entity-owned project");
      }
      return [{ kind: audience, ref: project.owner.entityId }];
    case "named":
      return [...new Set([uploaderId, ...named])].map((ref) => ({ kind: "user", ref }));
    case "groups":
      return [
        { kind: "user", ref: uploaderId },
        ...[...new Set(picked.groups)].map((ref): Reader => ({ kind: "group", ref })),
        ...[...new Set(picked.rules)].map((ref): Reader => ({ kind: "attribute_rule", ref })),
      ];
  }
}

/** A short, plain description of a document's readers, for the files table. */
export function describeReaders(
  readers: readonly StoredReader[],
  project: Pick<ProjectView, "id" | "owner">,
  userId: string,
): string {
  const users = readers.filter((r) => r.kind === "user");
  const groups = readers.filter((r) => r.kind === "group").length;
  const rules = readers.filter((r) => r.kind === "attribute_rule").length;
  const parts: string[] = [];
  for (const reader of readers) {
    if (reader.kind === "project") {
      parts.push(t("readers.project"));
    } else if (reader.kind === "entity" || reader.kind === "entity_tree") {
      const owning = project.owner.kind === "entity" && project.owner.entityId === reader.ref;
      const ref = shortId(reader.ref);
      parts.push(
        reader.kind === "entity"
          ? owning
            ? t("readers.entity")
            : t("readers.entityRef", { ref })
          : owning
            ? t("readers.entityTree")
            : t("readers.entityTreeRef", { ref }),
      );
    }
    // Unknown kinds grant nothing, so they describe no one.
  }
  if (users.length > 0) {
    const onlyYou = users.length === 1 && users[0]?.ref === userId;
    parts.push(onlyYou ? t("readers.onlyYou") : t("readers.people", { count: users.length }));
  }
  if (groups > 0) {
    parts.push(t("readers.groups", { count: groups }));
  }
  if (rules > 0) {
    parts.push(t("readers.rules", { count: rules }));
  }
  return parts.length > 0 ? parts.join(", ") : t("readers.none");
}

/** A user ID as shown when no name is known. */
export const shortId = (id: string) => id.slice(0, 8);

/** A member's name from the project's member list, else their short ID. */
export function memberName(
  members: readonly { userId: string; displayName: string | null }[] | undefined,
  userId: string,
): string {
  return members?.find((m) => m.userId === userId)?.displayName ?? shortId(userId);
}
